<template>
  <div class="prohor-message" :class="rowClasses">
    <div v-if="message.role === 'assistant'" class="prohor-message__avatar prohor-message__avatar--bot">
      <img :src="prohorIcon" alt="Прохор" />
    </div>

    <div class="prohor-message__content">
      <div class="prohor-message__bubble" :class="bubbleClasses">
        <template v-if="isPending">
          <span class="prohor-message__typing">
            <span class="prohor-message__dot" />
            <span class="prohor-message__dot" />
            <span class="prohor-message__dot" />
          </span>
        </template>

        <template v-else>
          <p class="prohor-message__text">{{ message.text }}</p>
          <span class="prohor-message__time">{{ formattedTime }}</span>
        </template>
      </div>

      <div v-if="hasSuggestions && !isPending" class="prohor-message__suggestions">
        <button
            v-for="(suggestion, index) in message.suggestions"
            :key="index"
            type="button"
            class="prohor-message__suggestion"
            @click="$emit('suggestion', suggestion)"
        >
          {{ suggestion }}
        </button>
      </div>

      <div v-if="hasActions && !isPending" class="prohor-message__actions">
        <button
            v-for="(action, index) in message.actions"
            :key="`act-${index}`"
            type="button"
            class="prohor-message__action"
            @click="$emit('action', action)"
        >
          <span>{{ action.label || 'Перейти' }}</span>
          <ArrowRightOutlined />
        </button>
      </div>
    </div>

    <div v-if="message.role === 'user'" class="prohor-message__avatar prohor-message__avatar--user">
      <img :src="userAvatar" alt="Вы" />
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { ArrowRightOutlined } from "@ant-design/icons-vue";
import prohorIcon from "@/assets/images/icons/prohor_ai.svg";
import userAvatar from "@/assets/images/icons/avatar.svg";

export default {
  name: "ProhorMessage",
  components: { ArrowRightOutlined },
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  emits: ["suggestion", "action"],
  setup(props) {
    const isPending = computed(() => props.message.status === "pending");

    const hasSuggestions = computed(
        () =>
            Array.isArray(props.message.suggestions) &&
            props.message.suggestions.length > 0
    );

    const hasActions = computed(
        () =>
            Array.isArray(props.message.actions) &&
            props.message.actions.length > 0
    );

    const rowClasses = computed(() => ({
      "prohor-message--user": props.message.role === "user",
      "prohor-message--assistant": props.message.role === "assistant",
    }));

    const bubbleClasses = computed(() => ({
      "prohor-message__bubble--user": props.message.role === "user",
      "prohor-message__bubble--assistant": props.message.role === "assistant",
      "prohor-message__bubble--error": props.message.status === "error",
    }));

    const formattedTime = computed(() => {
      if (!props.message.createdAt) return "";
      try {
        const date = new Date(props.message.createdAt);
        return date.toLocaleTimeString("ru-RU", {
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return "";
      }
    });

    return {
      isPending,
      hasSuggestions,
      hasActions,
      rowClasses,
      bubbleClasses,
      formattedTime,
      prohorIcon,
      userAvatar,
    };
  },
};
</script>

<style scoped lang="scss">
.prohor-message {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;

  &--user {
    justify-content: flex-end;
  }

  &--assistant {
    justify-content: flex-start;
  }
}

.prohor-message__avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &--bot {
    background: var(--main-green);

    img {
      width: 18px;
      height: 14px;
    }
  }

  &--user {
    background: var(--geek-blue);
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px var(--geek-blue-3);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

.prohor-message__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: calc(100% - 80px);

  .prohor-message--user & {
    align-items: flex-end;
  }

  .prohor-message--assistant & {
    align-items: flex-start;
  }
}

.prohor-message__bubble {
  position: relative;
  padding: 10px 12px 22px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 20px;
  color: var(--main-font);
  word-wrap: break-word;

  &--assistant {
    background: rgba(0, 0, 0, 0.02);
    border: none;
  }

  &--user {
    background: rgba(0, 0, 0, 0.06);
    border: none;
  }

  &--error {
    background: var(--dust-red);
    border: none;
    color: var(--dist-red-main);
  }
}

.prohor-message__text {
  margin: 0;
  white-space: pre-wrap;
}

.prohor-message__time {
  position: absolute;
  right: 10px;
  bottom: 6px;
  font-size: 11px;
  line-height: 1;
  color: var(--neutral-text-description);
}

.prohor-message__suggestions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
}

.prohor-message__suggestion {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid var(--main-green);
  background: #fff;
  color: var(--main-green);
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--focus-color);
  }
}

.prohor-message__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;

  .prohor-message--assistant & {
    justify-content: flex-start;
  }

  .prohor-message--user & {
    justify-content: flex-end;
  }
}

.prohor-message__action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid var(--main-green);
  background: var(--main-green);
  color: #fff;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #fff;
    color: var(--main-green);
  }
}

.prohor-message__typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
}

.prohor-message__dot {
  width: 6px;
  height: 6px;
  background: var(--grey-text);
  border-radius: 50%;
  opacity: 0.4;
  animation: prohor-typing 1.2s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }
}

@keyframes prohor-typing {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-3px);
    opacity: 1;
  }
}
</style>
